import { useEffect, useState } from 'react';

import { RecordPageShell } from '~/components/layout';
import {
  Alert,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  CardSection,
  ConfirmModal,
  FormActions,
} from '~/components/ui';
import { TextField, ToggleField } from '~/components/form';
import { useFormValidation } from '~/hooks/useFormValidation';
import { useUnsavedGuard } from '~/hooks/useUnsavedGuard';
import { onboardSchema } from '~/lib/onboardForm';
import { isDirty } from '~/lib/recordDraft';
import { routes } from '~/app/routes';
import { DomainCard, type HarnessProgress } from '~/components/pages/domain/components/domainCard';
import { queryErrorMessage } from '~/modules/query/base';
import {
  useCreateOnboardingMutation,
  useGetOnboardingProgressQuery,
  useLazyGetSkeletonQuery,
} from '~/modules/query/onboarding';

const ONBOARDING_SUBTITLE =
  'Restores the template database, creates symlinks and certs, sets up Rackspace DNS and SendGrid. Progress is tracked per step; a failed run resumes from the point of failure when resubmitted.';
const FORM_ID = 'onboard-form';
const PROGRESS_POLL_MS = 5000;

const stepFailedIn = (progress?: HarnessProgress) =>
  Object.values(progress?.statusesOfSteps ?? {}).some(
    (step) => step?.result?.isErr && !step?.isSkipped,
  );

/** A run is over when it succeeded, or ended without running and either failed a step or is done. */
const runFinished = (progress?: HarnessProgress) =>
  Boolean(progress?.isOk || (progress?.isDone && !progress?.isRunning) || stepFailedIn(progress));

interface FieldDef {
  name: string;
  label: string;
  required?: boolean;
  placeholder?: string;
}

const FIELDS: FieldDef[] = [
  {
    name: 'customerMoniker',
    label: 'Customer moniker',
    required: true,
    placeholder: 'e.g. acmerealty',
  },
  { name: 'customerName', label: 'Customer name', required: true, placeholder: 'e.g. Acme Realty' },
  {
    name: 'customerDbSubdomain',
    label: 'Customer DB subdomain',
    required: true,
    placeholder: 'e.g. cust-sql-0',
  },
  {
    name: 'clientBayDomain',
    label: 'ClientBAY domain',
    required: true,
    placeholder: 'e.g. acmerealty.com',
  },
  {
    name: 'sitesBayDomain',
    label: 'SitesBAY domain',
    required: true,
    placeholder: 'e.g. homes.acmerealty.com',
  },
  {
    name: 'sitesBayRootDomain',
    label: 'SitesBAY root domain',
    placeholder: 'defaults from SitesBAY domain',
  },
  {
    name: 'clientBayCfcHost',
    label: 'ClientBAY CFC host',
    placeholder: 'defaults to www.<ClientBAY domain>',
  },
  { name: 'mlsMoniker', label: 'MLS moniker', placeholder: 'optional' },
  { name: 'symlinkType', label: 'Symlink type', placeholder: 'optional' },
];

const OFFICE_FIELDS: FieldDef[] = [
  { name: 'address', label: 'Address' },
  { name: 'city', label: 'City' },
  { name: 'state', label: 'State' },
  { name: 'zip', label: 'Zip' },
  { name: 'phone', label: 'Phone' },
  { name: 'fax', label: 'Fax' },
  { name: 'tollFree', label: 'Toll free' },
];

type OnboardingFormValues = { [field: string]: string | boolean; raze: boolean };

const initialValues: OnboardingFormValues = {
  customerMoniker: '',
  customerName: '',
  customerDbSubdomain: '',
  clientBayDomain: '',
  sitesBayDomain: '',
  sitesBayRootDomain: '',
  clientBayCfcHost: '',
  mlsMoniker: '',
  symlinkType: '',
  raze: false,
  ...Object.fromEntries(OFFICE_FIELDS.map(({ name }) => [`office_${name}`, ''])),
};

/** `/customers/new`: the onboarding form, then — once started — the run's step progress on the same page. */
export const OnboardPage = () => {
  const [started, setStarted] = useState(false);
  const [razeConfirmOpen, setRazeConfirmOpen] = useState(false);
  const [activeMoniker, setActiveMoniker] = useState<string | null>(null);
  const [displayLoop, setDisplayLoop] = useState<HarnessProgress>({});
  const [setupError, setSetupError] = useState<string | null>(null);
  const [getSkeleton] = useLazyGetSkeletonQuery();
  const [createOnboarding] = useCreateOnboardingMutation();

  // The run's step progress, polled until the run ends. The card keeps the last values, so a
  // failed poll never blanks it. A resubmit re-subscribes the same moniker, whose cached progress
  // is stale, so the subscription always fetches.
  const { data: progress } = useGetOnboardingProgressQuery(activeMoniker ?? '', {
    pollingInterval: runFinished(displayLoop) ? 0 : PROGRESS_POLL_MS,
    skip: !activeMoniker,
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    if (progress) setDisplayLoop(progress);
  }, [progress]);

  const backToForm = () => {
    setStarted(false);
    setActiveMoniker(null);
    setDisplayLoop({});
    setSetupError(null);
  };

  const startOnboarding = async (values: OnboardingFormValues) => {
    const payload = {
      customer: { moniker: values.customerMoniker as string, name: values.customerName },
      customerDbSubdomain: values.customerDbSubdomain,
      clientBayDomain: values.clientBayDomain,
      sitesBayDomain: values.sitesBayDomain,
      sitesBayRootDomain: values.sitesBayRootDomain || undefined,
      clientBayCfcHost: values.clientBayCfcHost || `www.${values.clientBayDomain}`,
      mlsMoniker: values.mlsMoniker || undefined,
      symlinkType: values.symlinkType || undefined,
      office: Object.fromEntries(
        OFFICE_FIELDS.map(({ name }) => [name, values[`office_${name}`] || undefined]),
      ),
    };

    try {
      // The skeleton names the steps before the first progress poll returns.
      const skeleton = await getSkeleton()
        .unwrap()
        .catch(() => null);
      if (skeleton) setDisplayLoop(skeleton);

      const res = await createOnboarding({ payload, raze: Boolean(values.raze) }).unwrap();
      if (res.status === 0) {
        setActiveMoniker(values.customerMoniker as string);
      } else {
        setSetupError(queryErrorMessage(res) || 'Onboarding could not be started. Try again.');
      }
    } catch (err) {
      console.error(err);
      setSetupError(queryErrorMessage(err) || 'Onboarding could not be started. Try again.');
    }
  };

  // The values survive a failed run, so a resubmit resumes it instead of a retyped (and possibly new) moniker.
  const [values, setValues] = useState<OnboardingFormValues>(initialValues);
  const validation = useFormValidation(onboardSchema, values);
  const setValue = (name: string, value: string | boolean) =>
    setValues((current) => ({ ...current, [name]: value }));

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!validation.submit()) return;
    if (values.raze) setRazeConfirmOpen(true);
    else begin(values);
  };

  const begin = (values: OnboardingFormValues) => {
    // Raze never carries over: a resubmit resumes unless the operator turns it back on.
    setValue('raze', false);
    setStarted(true);
    startOnboarding(values);
  };

  // "Back to form" only once the run has ended without success; mid-run, a resubmit would start a second concurrent run of the same moniker.
  const stepFailed = stepFailedIn(displayLoop);
  const runFailed =
    Boolean(setupError) ||
    (!displayLoop.isOk && (stepFailed || Boolean(displayLoop.isDone && !displayLoop.isRunning)));
  const running = started && !runFailed && !displayLoop.isOk;

  const guard = useUnsavedGuard(
    started ? running : isDirty(values, initialValues),
    running
      ? 'Onboarding is still running. It continues on the server, but you lose this progress view.'
      : undefined,
  );

  return (
    <RecordPageShell
      title="Onboard customer"
      description={started ? undefined : ONBOARDING_SUBTITLE}
      breadcrumbs={[{ label: 'Customers', href: routes.customers }, { label: 'Onboard' }]}
      guard={guard}
      testId="onboard"
    >
      {started ? (
        <Card>
          <CardHeader title="Onboarding progress" />
          <CardBody>
            {setupError ? (
              <Alert variant="error">
                <p className="text-sm text-gray-800 dark:text-white/90">{setupError}</p>
              </Alert>
            ) : (
              <DomainCard displayLoop={displayLoop} />
            )}
          </CardBody>
          <CardFooter>
            <FormActions
              discardHref={routes.customers}
              discardLabel="Back to customers"
              saveLabel={displayLoop.isOk ? 'Onboard another customer' : 'Back to form'}
              disabled={running}
              disabledReason="Onboarding is still running"
              onSave={() => {
                if (displayLoop.isOk) {
                  setValues(initialValues);
                  validation.reset();
                }
                backToForm();
              }}
              saveTestId="onboarding-back-button"
            />
          </CardFooter>
        </Card>
      ) : (
        <form id={FORM_ID} noValidate onSubmit={submit}>
          <Card>
            <CardBody className="space-y-6">
              <CardSection title="Customer">
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                  {FIELDS.map(({ name, label, required, placeholder }) => (
                    <TextField
                      key={name}
                      id={name}
                      name={name}
                      testId={`onboarding-${name}-input`}
                      label={label}
                      required={required}
                      placeholder={placeholder}
                      value={values[name] as string}
                      onChange={(value) => setValue(name, value)}
                      onBlur={() => validation.touch(name)}
                      error={validation.errors[name]}
                    />
                  ))}
                </div>
              </CardSection>

              <CardSection title="Office">
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                  {OFFICE_FIELDS.map(({ name, label }) => (
                    <TextField
                      key={name}
                      id={`office_${name}`}
                      name={`office_${name}`}
                      testId={`onboarding-office-${name}-input`}
                      label={label}
                      value={values[`office_${name}`] as string}
                      onChange={(value) => setValue(`office_${name}`, value)}
                    />
                  ))}
                </div>
              </CardSection>

              <CardSection title="Existing data">
                <ToggleField
                  id="onboarding-raze"
                  testId="onboarding-raze-toggle"
                  text="Delete existing customer data first"
                  helperText="Permanently removes the customer's current database and provider resources, then recreates everything from scratch. Leave off to keep existing data."
                  checked={values.raze}
                  onChange={(checked) => setValue('raze', checked)}
                />
              </CardSection>
            </CardBody>
            <CardFooter>
              <FormActions
                form={FORM_ID}
                discardHref={routes.customers}
                discardLabel="Cancel"
                saveLabel="Start onboarding"
                saveTestId="onboarding-start-button"
              />
            </CardFooter>
          </Card>
        </form>
      )}

      <ConfirmModal
        isOpen={razeConfirmOpen}
        variant="danger"
        title="Delete existing customer data?"
        message={`RAZE will permanently delete customer '${values.customerMoniker}' — database and provider resources — then recreate everything.`}
        dangerNote="The customer's current database and provider resources cannot be recovered."
        confirmText={values.customerMoniker as string}
        confirmLabel="Delete and onboard"
        testId="onboard-raze-confirm"
        onClose={() => setRazeConfirmOpen(false)}
        onConfirm={() => {
          setRazeConfirmOpen(false);
          begin(values);
        }}
      />
    </RecordPageShell>
  );
};
